/**
 * Owner portal · one room, read-only.
 *
 * The view behind the property overview's room link. Before it existed,
 * clicking a room opened the TENANT dialog — rooms and tenants were the same
 * click. They are two things, so they are now two views: the room name comes
 * here, the tenant name opens the tenant panel.
 *
 * Read-only like the rest of the owner portal: it states what the room is, who
 * lives in it, and who used to. Uploading documents against a room is a
 * separate change.
 */
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOwnerPropertyAccess } from "@/hooks/v2/useOwnerPropertyAccess";
import { useOwnerRoomDetail } from "@/hooks/owner/useOwnerPropertyDetail";
import { OwnerTenantDetailDialog } from "@/components/owner/OwnerTenantDetailDialog";
import { V2AdminNavbar } from "@/components/admin/v2/V2AdminNavbar";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CalendarClock, ChevronRight, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { PAGE_TITLE_CLASS } from "@/components/admin/v2/page-title";

function formatEuro(amount: number | null | undefined, locale: string) {
  if (amount == null) return "—";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(value: string | null | undefined, locale: string) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function OwnerRoomDetail() {
  const { roomId } = useParams<{ roomId: string }>();
  const id = roomId ? Number(roomId) : null;
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-DE" : "en-GB";
  const { isOwnerPortalOnly } = useAuth();
  /** booking_id of the tenancy whose panel is open — the current tenant or a
   *  past one; both open the same dialog, which fetches by booking_id. */
  const [viewingBookingId, setViewingBookingId] = useState<number | null>(null);

  const { data, isLoading, isError, error } = useOwnerRoomDetail(id);
  const room = data?.room ?? null;
  const tenant = data?.current_tenant ?? null;
  const history = data?.history ?? [];
  const pastTenant =
    history.find((h) => h.booking_id === viewingBookingId) ?? null;

  // The room's property, once known — the access guard and the breadcrumb both
  // need it, and neither can ask before the RPC answers.
  const access = useOwnerPropertyAccess(
    isOwnerPortalOnly ? (room?.property_id ?? null) : null
  );

  if (isOwnerPortalOnly && access.data === false) {
    return <Navigate to="/admin?tab=analytics" replace />;
  }

  const notFound = !isLoading && !isError && data === null;

  return (
    <div className="min-h-screen bg-background">
      <V2AdminNavbar />
      <div className="mx-auto w-full max-w-[1920px] px-4 sm:px-6 lg:px-8 pt-6 pb-12 space-y-6">
        <nav
          aria-label={t("owner.breadcrumbAriaLabel", "Breadcrumb")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Link
            to="/admin?tab=analytics"
            className="hover:text-foreground transition-colors"
          >
            {t("admin.nav.ownerAnalytics", "Analytics")}
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          {room?.property_id ? (
            <Link
              to={`/admin/owner/property/${room.property_id}`}
              className="hover:text-foreground transition-colors truncate"
            >
              {room.property_title ?? t("owner.detail.property", "Property")}
            </Link>
          ) : null}
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground font-medium truncate">
            {room?.title ?? t("owner.detail.room", "Room")}
          </span>
        </nav>

        {isError ? (
          <Alert variant="destructive" className="rounded-xl">
            <AlertTitle>
              {t("owner.room.loadErrorTitle", "Could not load room")}
            </AlertTitle>
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : t(
                    "owner.room.loadErrorHint",
                    "Apply the latest Supabase migrations so the owner_room_detail function exists, then retry."
                  )}
            </AlertDescription>
          </Alert>
        ) : null}

        {notFound ? (
          <Alert className="rounded-xl">
            <AlertTitle>
              {t("owner.room.notFoundTitle", "Room unavailable")}
            </AlertTitle>
            <AlertDescription>
              {t(
                "owner.room.notFoundHint",
                "This room may have been removed, or it is in a property that is not assigned to your account."
              )}
            </AlertDescription>
          </Alert>
        ) : null}

        {/* Header */}
        <div className="space-y-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {isLoading ? (
              <Skeleton className="h-8 w-64 rounded-lg" />
            ) : (
              <h1 className={`flex items-center gap-2.5 ${PAGE_TITLE_CLASS}`}>
                {room?.title ?? t("owner.detail.room", "Room")}
                {room?.apartment_label ? (
                  <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                    {room.apartment_label}
                  </span>
                ) : null}
              </h1>
            )}
          </div>
          {room ? (
            <p className="text-sm text-muted-foreground">
              {[
                room.floor,
                room.size_sqm != null ? `${room.size_sqm} m²` : null,
                formatEuro(room.monthly_rent, locale),
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          ) : null}
        </div>

        {/* Who lives here */}
        <PanelCard
          title={t("owner.room.currentTenant", "Current tenant")}
          icon={<User className="h-4 w-4" />}
        >
          {isLoading ? (
            <div className="px-5 pb-5 space-y-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-4 w-64" />
            </div>
          ) : !tenant ? (
            <p className="px-5 pb-6 pt-1 text-sm text-muted-foreground">
              {t("owner.room.vacant", "This room is empty right now.")}
            </p>
          ) : (
            <div className="px-5 pb-5 space-y-3">
              <button
                type="button"
                onClick={() => setViewingBookingId(tenant.booking_id)}
                className="text-base font-semibold text-teal-700 hover:underline"
              >
                {tenant.name ?? t("owner.detail.tenant", "Tenant")}
              </button>
              <dl className="grid gap-3 sm:grid-cols-3">
                <Fact
                  label={t("owner.detail.colMovein", "Move-in")}
                  value={formatDate(tenant.move_in_date, locale)}
                />
                <Fact
                  label={t("owner.detail.colMoveout", "Move-out")}
                  value={formatDate(tenant.move_out_date, locale)}
                />
                <Fact
                  label={t("owner.detail.colRent", "Rent")}
                  value={formatEuro(tenant.monthly_rent, locale)}
                />
              </dl>
            </div>
          )}
        </PanelCard>

        {/* Who used to live here */}
        <Card className="rounded-2xl border-border/60 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 border-b bg-muted/15 px-5 py-3">
            <span className="text-muted-foreground">
              <CalendarClock className="h-4 w-4" />
            </span>
            <h2 className="text-sm font-semibold">
              {t("owner.detail.pastTenancies", "Past tenancies")} (
              {history.length})
            </h2>
          </div>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full rounded" />
                ))}
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">
                {t(
                  "owner.room.noHistory",
                  "Nobody has moved out of this room yet."
                )}
              </p>
            ) : (
              <div className="w-full overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>
                        {t("owner.detail.colTenant", "Tenant")}
                      </TableHead>
                      <TableHead>
                        {t("owner.detail.colMovein", "Move-in")}
                      </TableHead>
                      <TableHead>
                        {t("owner.detail.colMoveout", "Move-out")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((h) => (
                      <TableRow key={h.booking_id}>
                        <TableCell className="font-medium">
                          <button
                            type="button"
                            onClick={() => setViewingBookingId(h.booking_id)}
                            className="text-left font-medium text-teal-700 hover:underline"
                          >
                            {h.name ?? t("owner.detail.tenant", "Tenant")}
                          </button>
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {formatDate(h.move_in_date, locale)}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {formatDate(h.move_out_date, locale)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* The occupant's full panel — the same dialog the property overview
          opens, driven by booking_id. */}
      <OwnerTenantDetailDialog
        tenant={
          tenant && viewingBookingId === tenant.booking_id
            ? {
                booking_id: tenant.booking_id,
                name: tenant.name,
                email: tenant.email,
                phone: tenant.phone,
                room_id: room?.id ?? null,
                room_title: room?.title ?? null,
                move_in_date: tenant.move_in_date,
                move_out_date: tenant.move_out_date,
                status: null,
                contract_status: tenant.contract_status,
                signed_contract_url: tenant.signed_contract_url,
                payment_status: tenant.payment_status,
                monthly_rent: tenant.monthly_rent,
                deposit_amount: tenant.deposit_amount,
                tenant_active: tenant.tenant_active,
                rent_due_date: tenant.rent_due_date,
                last_payment_received_at: tenant.last_payment_received_at,
                rent_paid_through: tenant.rent_paid_through,
                manually_marked_paid: tenant.manually_marked_paid,
                reminders_enabled: tenant.reminders_enabled,
              }
            : pastTenant
              ? {
                  // A past tenant has no current-tenant payload; the dialog
                  // fetches the full record by booking_id and shows these
                  // fields as placeholders until it lands.
                  booking_id: pastTenant.booking_id,
                  name: pastTenant.name,
                  email: null,
                  phone: null,
                  room_id: room?.id ?? null,
                  room_title: room?.title ?? null,
                  move_in_date: pastTenant.move_in_date,
                  move_out_date: pastTenant.move_out_date,
                  status: null,
                  contract_status: null,
                  signed_contract_url: null,
                  payment_status: null,
                  monthly_rent: null,
                  deposit_amount: null,
                  tenant_active: false,
                  rent_due_date: null,
                  last_payment_received_at: null,
                  rent_paid_through: null,
                  manually_marked_paid: null,
                  reminders_enabled: null,
                }
              : null
        }
        propertyId={room?.property_id ?? null}
        propertyTitle={room?.property_title ?? ""}
        open={viewingBookingId != null && (!!tenant || !!pastTenant)}
        onOpenChange={(o) => !o && setViewingBookingId(null)}
      />
    </div>
  );
}

function PanelCard({
  title,
  icon,
  className,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      className={cn(
        "rounded-2xl border-border/60 shadow-sm overflow-hidden",
        className
      )}
    >
      <div className="flex items-center gap-2 px-5 pt-5 pb-3">
        {icon ? <span className="text-muted-foreground">{icon}</span> : null}
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
      </div>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}

export default OwnerRoomDetail;
